import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowRight, CalendarDays, Inbox, Loader2, Receipt, Trophy, UserPlus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState, Panel, PersonAvatar, adminInput, adminLink } from '@/components/admin/ui';
import { useDashboardInsights } from '@/hooks/useDashboardInsights';
import { INSIGHT_RANGES, type InsightRange, type Insights } from '@/lib/dashboard-insights';
import { formatMoney } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { CHART, ColumnChart, ComparisonChart, Delta, GaugeChart, LegendMark, TipRow } from './charts';

type Currency = Parameters<typeof formatMoney>[1];

const PIPELINE_COLORS: Record<string, string> = {
  pending: CHART.amber,
  contacted: CHART.blue,
  quoted: CHART.violet,
  converted: CHART.teal,
  declined: CHART.prev,
};

/** Overdue ageing is ordinal, so it uses one warm ramp (calm → urgent). */
const AGING_COLORS = ['var(--adm-c-prev)', 'hsl(28 88% 66%)', 'hsl(14 80% 56%)', 'hsl(354 70% 46%)'];

const pct = (v: number) => `${Math.round(v)}%`;
const rangeText = (start: string, end: string) => {
  const s = parseISO(start);
  const e = parseISO(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  return `${format(s, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${format(e, 'd MMM yyyy')}`;
};

export function InsightsView({
  orgId,
  currency,
  range,
  onRangeChange,
}: {
  orgId: string | null | undefined;
  currency: Currency;
  range: InsightRange;
  onRangeChange: (range: InsightRange) => void;
}) {
  const { data, isLoading, isFetching, isError, refetch } = useDashboardInsights(orgId, range);

  const fmt = (v: number) => formatMoney(v, currency, { maximumFractionDigits: 0 });
  const symbol = currency?.symbol ?? '£';
  const axisMoney = (v: number) =>
    Math.abs(v) >= 1000 ? `${symbol}${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}k` : `${symbol}${Math.round(v)}`;
  const rangeLabel = INSIGHT_RANGES.find((r) => r.value === range)?.label ?? '';
  const variant = data && data.window.granularity === 'day' && data.window.buckets.length > 7 ? 'bars' : 'line';

  return (
    <div className="space-y-4" data-testid="dashboard-insights">
      {/* Toolbar: Fikri "Insight  [Last 7 days] [dates] compared to [Previous period]" */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={range} onValueChange={(v) => onRangeChange(v as InsightRange)}>
          <SelectTrigger className={cn(adminInput, 'w-[150px] font-medium')} aria-label="Report period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INSIGHT_RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <>
            <span className="inline-flex h-9 items-center gap-2 rounded-[9px] border border-border bg-card px-3 text-[13px] text-foreground shadow-[var(--adm-shadow-xs)]">
              <CalendarDays className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {rangeText(data.window.start, data.window.end)}
            </span>
            <span className="adm-range-sep px-1">compared to</span>
            <span
              className="inline-flex h-9 items-center gap-2 rounded-[9px] border border-dashed border-border px-3 text-[13px] text-muted-foreground"
              title={rangeText(data.window.prevStart, data.window.prevEnd)}
            >
              <LegendMark color={CHART.prev} dashed />
              Previous period
            </span>
          </>
        )}
        {isFetching && !isLoading && (
          <Loader2 className="ml-1 h-4 w-4 animate-spin text-muted-foreground" aria-label="Updating" />
        )}
      </div>

      {isError ? (
        <Panel>
          <EmptyState
            icon={<Receipt />}
            title="Couldn't load your report"
            description="Check your connection and try again."
            action={
              <button type="button" onClick={() => refetch()} className={adminLink}>
                Try again
              </button>
            }
          />
        </Panel>
      ) : isLoading || !data ? (
        <InsightsSkeleton />
      ) : (
        <div className={cn('space-y-4 transition-opacity', isFetching && 'opacity-70')}>
          <Headline insights={data.insights} fmt={fmt} rangeLabel={rangeLabel} />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <Panel
              title="Revenue"
              description="Booked services, excluding cancellations"
              actions={<Delta value={data.insights.revenue.change} />}
            >
              <PeriodTotals
                color={CHART.violet}
                current={fmt(data.insights.revenue.current)}
                previous={fmt(data.insights.revenue.previous)}
              />
              <ComparisonChart
                variant={variant}
                color={CHART.violet}
                format={fmt}
                axisFormat={axisMoney}
                data={data.insights.series.map((p) => ({ ...p, current: p.revenue, previous: p.prevRevenue }))}
              />
            </Panel>

            <Panel
              title="Jobs"
              description="Jobs booked in, excluding cancellations"
              actions={<Delta value={data.insights.jobs.change} />}
            >
              <PeriodTotals
                color={CHART.teal}
                current={String(data.insights.jobs.current)}
                previous={String(data.insights.jobs.previous)}
              />
              <ComparisonChart
                variant={variant}
                color={CHART.teal}
                format={(v) => `${v} ${v === 1 ? 'job' : 'jobs'}`}
                axisFormat={(v) => String(v)}
                data={data.insights.series.map((p) => ({ ...p, current: p.jobs, previous: p.prevJobs }))}
              />
            </Panel>

            <JobOutcomes insights={data.insights} className="lg:col-span-2 xl:col-span-1" />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <EnquiryPipeline insights={data.insights} className="xl:col-span-2" />
            <LeadSources insights={data.insights} />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
            <OutstandingInvoices insights={data.insights} fmt={fmt} />
            <RevenueByService insights={data.insights} fmt={fmt} />
            <Panel title="Busiest days" description="Jobs by day of the week">
              {data.insights.jobs.current === 0 ? (
                <EmptyState compact icon={<CalendarDays />} title="No jobs in this period" />
              ) : (
                <ColumnChart
                  data={data.insights.byWeekday}
                  xKey="day"
                  yKey="jobs"
                  color={CHART.teal}
                  tooltip={(d) => <TipRow color={CHART.teal} label={d.day} value={`${d.jobs} jobs`} />}
                />
              )}
            </Panel>
            <TopCleaners insights={data.insights} fmt={fmt} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PeriodTotals({ color, current, previous }: { color: string; current: string; previous: string }) {
  return (
    <div className="mb-2 flex flex-wrap gap-x-8 gap-y-2">
      <div>
        <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          <LegendMark color={color} />
          This period
        </p>
        <p className="adm-num mt-0.5 text-[20px] font-semibold tracking-tight text-foreground">{current}</p>
      </div>
      <div>
        <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          <LegendMark color={CHART.prev} dashed />
          Previous period
        </p>
        <p className="adm-num mt-0.5 text-[20px] font-semibold tracking-tight text-muted-foreground">{previous}</p>
      </div>
    </div>
  );
}

function Headline({ insights, fmt, rangeLabel }: { insights: Insights; fmt: (v: number) => string; rangeLabel: string }) {
  const hadEnquiries = insights.enquiries.previous > 0;
  const hadJobs = insights.jobs.previous > 0;
  const tiles = [
    {
      label: 'Jobs completed',
      value: String(insights.completed.current),
      delta: <Delta value={insights.completed.change} />,
      accent: CHART.teal,
    },
    {
      label: 'Average job value',
      value: fmt(insights.avgJobValue.current),
      delta: <Delta value={insights.avgJobValue.change} />,
      accent: CHART.violet,
    },
    {
      label: 'New customers',
      value: String(insights.newCustomers.current),
      delta: <Delta value={insights.newCustomers.change} />,
      accent: CHART.blue,
    },
    {
      label: 'Repeat customers',
      value: pct(insights.repeatRate.current),
      delta: <Delta unit="pts" value={hadJobs ? insights.repeatRate.current - insights.repeatRate.previous : null} />,
      accent: CHART.amber,
    },
    {
      label: 'Enquiries booked',
      value: pct(insights.conversion.current),
      sub: `${insights.pipeline.stages.find((s) => s.key === 'converted')?.value ?? 0} of ${insights.enquiries.current}`,
      delta: <Delta unit="pts" value={hadEnquiries ? insights.conversion.current - insights.conversion.previous : null} />,
      accent: CHART.pink,
    },
  ];

  return (
    <section className="adm-panel p-4 md:p-5" aria-label="Headline figures">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-3 xl:grid-cols-[1.35fr_repeat(5,minmax(0,1fr))] xl:items-end">
        <div className="col-span-2 md:col-span-3 xl:col-span-1">
          <p className="text-[12.5px] text-muted-foreground">Booked revenue · {rangeLabel.toLowerCase()}</p>
          <p className="adm-num mt-1 text-[32px] font-semibold leading-none tracking-tight text-foreground" data-testid="insights-revenue">
            {fmt(insights.revenue.current)}
          </p>
          <div className="mt-2 flex items-center gap-2 text-[12px] text-muted-foreground">
            <Delta value={insights.revenue.change} />
            <span>
              {insights.revenue.previous > 0
                ? `vs ${fmt(insights.revenue.previous)} previous period`
                : 'No bookings in the previous period to compare'}
            </span>
          </div>
        </div>
        {tiles.map((t) => (
          <div key={t.label} className="min-w-0 border-l-2 pl-3" style={{ borderColor: t.accent }}>
            <p className="truncate text-[12px] text-muted-foreground">{t.label}</p>
            <p className="adm-num mt-1 text-[20px] font-semibold leading-tight tracking-tight text-foreground">{t.value}</p>
            <div className="mt-1 flex min-h-[20px] items-center gap-1.5 text-[11.5px] text-muted-foreground">
              {t.delta}
              {t.sub && <span className="truncate">{t.sub}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function JobOutcomes({ insights, className }: { insights: Insights; className?: string }) {
  const colors = { completed: CHART.teal, open: CHART.blue, cancelled: CHART.pink } as const;
  const segments = insights.outcomes.map((o) => ({ ...o, color: colors[o.key] }));
  const total = segments.reduce((t, s) => t + s.value, 0);
  return (
    <Panel title="Job outcomes" description="Every job scheduled in this period" className={className}>
      <GaugeChart segments={segments} total={total} caption="Total jobs" />
      <ul className="mt-4 space-y-2">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-3 text-[12.5px]">
            <span className="flex items-center gap-2 text-muted-foreground">
              <LegendMark shape="square" color={s.color} />
              {s.label}
            </span>
            <span className="adm-num text-muted-foreground">
              <span className="font-semibold text-foreground">{s.value}</span>
              <span className="ml-2 inline-block w-9 text-right">{total ? pct((s.value / total) * 100) : '0%'}</span>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function EnquiryPipeline({ insights, className }: { insights: Insights; className?: string }) {
  const { pipeline } = insights;
  const rows = pipeline.byService.slice(0, 5);
  return (
    <Panel
      title="Enquiry pipeline"
      description={`${pipeline.total} ${pipeline.total === 1 ? 'enquiry' : 'enquiries'} received, by service and where each one is now`}
      className={className}
      actions={
        <Link to="/admin/enquiries" className={adminLink} aria-label="Open enquiries">
          Enquiries <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      }
    >
      <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-5">
        {pipeline.stages.map((s) => (
          <div key={s.key} className="min-w-0 rounded-xl bg-muted/60 px-3 py-2">
            <p className="flex items-center gap-1.5 truncate text-[11.5px] text-muted-foreground">
              <LegendMark shape="square" color={PIPELINE_COLORS[s.key]} />
              {s.label}
            </p>
            <p className="adm-num mt-0.5 text-[18px] font-semibold text-foreground">{s.value}</p>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <EmptyState compact icon={<Inbox />} title="No enquiries in this period" description="New website enquiries will show up here." />
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="grid grid-cols-[minmax(0,150px)_1fr] items-center gap-3 sm:grid-cols-[minmax(0,190px)_1fr]">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-foreground">{row.name}</p>
                <p className="text-[11.5px] text-muted-foreground">
                  {row.total} {row.total === 1 ? 'enquiry' : 'enquiries'}
                </p>
              </div>
              <div className="flex" role="img" aria-label={pipeline.stages.map((s) => `${s.label} ${row.counts[s.key]}`).join(', ')}>
                {pipeline.stages
                  .filter((s) => row.counts[s.key] > 0)
                  .map((s) => (
                    <div
                      key={s.key}
                      className="adm-chevron adm-num"
                      title={`${s.label}: ${row.counts[s.key]}`}
                      style={{
                        background: PIPELINE_COLORS[s.key],
                        flexGrow: row.counts[s.key],
                        flexBasis: 34,
                        color: s.key === 'declined' ? 'hsl(var(--foreground))' : '#fff',
                      }}
                    >
                      {row.counts[s.key]}
                    </div>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Conversion by lead source: one bar per source, its length the number of
 * enquiries, the solid part the ones that became bookings. Sources sit in a
 * fixed order of size, so colour never has to carry identity.
 */
function LeadSources({ insights, className }: { insights: Insights; className?: string }) {
  const rows = insights.bySource;
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <Panel
      title="Leads by source"
      description="Where this period's enquiries came from, and how many were booked"
      className={className}
    >
      {rows.length === 0 ? (
        <EmptyState compact icon={<Inbox />} title="No enquiries in this period" description="Set a lead source on each enquiry to see what's working." />
      ) : (
        <>
          <div className="mb-3 flex items-center gap-4 text-[11.5px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <LegendMark shape="square" color={CHART.teal} />
              Booked
            </span>
            <span className="flex items-center gap-1.5">
              <LegendMark shape="square" color={CHART.prev} />
              Not booked yet
            </span>
          </div>
          <ul className="space-y-3.5" data-testid="insights-lead-sources">
            {rows.map((r) => (
              <li key={r.key}>
                <div className="mb-1 flex items-baseline justify-between gap-3 text-[12.5px]">
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-foreground">{r.label}</span>
                    <span className="text-muted-foreground">
                      {' '}· {r.total} {r.total === 1 ? 'lead' : 'leads'}
                    </span>
                  </span>
                  <span className="adm-num shrink-0 text-muted-foreground">
                    <span className="font-semibold text-foreground">{pct(r.rate)}</span> booked
                  </span>
                </div>
                <div
                  className="flex h-2 gap-[2px]"
                  style={{ width: `${Math.max(4, (r.total / max) * 100)}%` }}
                  role="img"
                  aria-label={`${r.label}: ${r.converted} of ${r.total} booked`}
                  title={`${r.label}: ${r.converted} of ${r.total} booked (${pct(r.rate)})`}
                >
                  {r.converted > 0 && (
                    <div className="h-full rounded-full" style={{ flexGrow: r.converted, background: CHART.teal }} />
                  )}
                  {r.total - r.converted > 0 && (
                    <div className="h-full rounded-full" style={{ flexGrow: r.total - r.converted, background: CHART.prev }} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}

function OutstandingInvoices({ insights, fmt }: { insights: Insights; fmt: (v: number) => string }) {
  const { outstanding } = insights;
  return (
    <Panel
      title="Outstanding invoices"
      description="Unpaid balances today, by how overdue they are"
      actions={
        <Link to="/admin/invoices" className={adminLink} aria-label="Open invoices">
          Invoices <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      }
    >
      <p className="adm-num text-[24px] font-semibold tracking-tight text-foreground">{fmt(outstanding.total)}</p>
      <p className="text-[12px] text-muted-foreground">
        across {outstanding.count} {outstanding.count === 1 ? 'invoice' : 'invoices'}
      </p>
      <div className="mt-4 flex h-3 gap-[2px] overflow-hidden rounded-full bg-muted">
        {outstanding.total > 0 &&
          outstanding.buckets
            .map((b, i) => ({ ...b, color: AGING_COLORS[i] }))
            .filter((b) => b.amount > 0)
            .map((b) => (
              <div
                key={b.key}
                title={`${b.label}: ${fmt(b.amount)}`}
                style={{ width: `${(b.amount / outstanding.total) * 100}%`, background: b.color }}
              />
            ))}
      </div>
      <ul className="mt-4 space-y-2.5">
        {outstanding.buckets.map((b, i) => (
          <li key={b.key} className="flex items-center justify-between gap-3 text-[12.5px]">
            <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
              <LegendMark shape="square" color={AGING_COLORS[i]} />
              <span className="truncate">{b.label}</span>
              {b.count > 0 && <span className="adm-num text-[11px]">({b.count})</span>}
            </span>
            <span className="adm-num font-semibold text-foreground">{fmt(b.amount)}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function RevenueByService({ insights, fmt }: { insights: Insights; fmt: (v: number) => string }) {
  const rows = insights.byService.slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r.revenue));
  const total = insights.revenue.current;
  return (
    <Panel title="Revenue by service" description="Where this period's money came from">
      {rows.length === 0 ? (
        <EmptyState compact icon={<Receipt />} title="No bookings in this period" />
      ) : (
        <ul className="space-y-4">
          {rows.map((r) => (
            <li key={r.id}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-[12.5px]">
                <span className="min-w-0 truncate">
                  <span className="font-medium text-foreground">{r.name}</span>
                  <span className="text-muted-foreground"> · {r.jobs} {r.jobs === 1 ? 'job' : 'jobs'}</span>
                </span>
                <span className="adm-num shrink-0 text-muted-foreground">
                  <span className="font-semibold text-foreground">{fmt(r.revenue)}</span>
                  <span className="ml-1.5">{total ? pct((r.revenue / total) * 100) : '—'}</span>
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${Math.max(2, (r.revenue / max) * 100)}%`, background: CHART.violet }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function TopCleaners({ insights, fmt, className }: { insights: Insights; fmt: (v: number) => string; className?: string }) {
  const rows = insights.team.slice(0, 5);
  return (
    <Panel
      title="Top cleaners"
      icon={<Trophy className="h-4 w-4 text-[color:var(--adm-c3)]" />}
      description="Ranked by jobs completed"
      className={className}
      actions={
        <Link to="/admin/team" className={adminLink}>
          Team
        </Link>
      }
    >
      {rows.length === 0 ? (
        <EmptyState compact icon={<UserPlus />} title="No assigned jobs yet" description="Assign jobs to your team to rank them here." />
      ) : (
        <ol className="space-y-3">
          {rows.map((m, i) => (
            <li key={m.id} className="flex items-center gap-3">
              <span
                className={cn(
                  'adm-num w-6 shrink-0 text-[12px] font-semibold',
                  i === 0 ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                #{i + 1}
              </span>
              <PersonAvatar name={m.name} seed={m.id} size={32} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-foreground">{m.name}</p>
                <p className="text-[11.5px] text-muted-foreground">
                  {m.jobs} {m.jobs === 1 ? 'job' : 'jobs'} · {fmt(m.revenue)}
                </p>
              </div>
              <span className="adm-chip adm-chip--mint adm-num shrink-0">{m.completed} done</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function InsightsSkeleton() {
  return (
    <div className="animate-pulse space-y-4" aria-hidden="true">
      <div className="h-[118px] rounded-2xl border border-border bg-card" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[330px] rounded-2xl border border-border bg-card" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="h-[300px] rounded-2xl border border-border bg-card xl:col-span-2" />
        <div className="h-[300px] rounded-2xl border border-border bg-card" />
      </div>
    </div>
  );
}
